/**
 * `useOemData()` — the lazy-data hook for the OEM+ port, plus the typed domain
 * map and the three single-document loaders.
 *
 * ## This file owns fetching, not shapes
 *
 * **Every row, record and payload shape lives in `src/versions/v1/schema/`** —
 * layer 1 of the 4-layer architecture owns type shapes, and nothing else may
 * declare them. This file owns exactly three things:
 *
 * 1. the {@link OemDomain} union and the domain→URL mapping,
 * 2. the module-level promise cache, and
 * 3. the React binding ({@link useOemData}) and the imperative loaders.
 *
 * {@link OemDomainData} is therefore assembled *entirely* from `schema/` types
 * — it maps each domain name onto the schema's `*Payload` interface for that
 * file, and composes the two domains that have no payload interface yet from
 * schema row types. It declares no shapes of its own.
 *
 * The schema types a scene author needs alongside the hook are **re-exported at
 * the foot of this file**, so `import { useOemData, type WorkOrder } from
 * './useOemData'` stays ergonomic without a second import line. The list is
 * explicit rather than `export *` so it is obvious what travels with the hook.
 *
 * ## What is lazy and why
 *
 * The OEM+ extraction splits the demo's 71 `B` keys into one **eager** file
 * (`src/versions/v1/data/core.json`, 116 KB, imported into the bundle — see
 * `data/core.ts`) and **twelve lazy domain files** plus two per-document
 * families, 5.90 MB across 384 files under `public/data/oem/`. None of the lazy
 * data is on the first-paint path; the largest single fetch is
 * `work-orders.json` at 811 KB (~157 KB gzipped over the wire).
 *
 * The domain→keys split is specified in **`scripts/oem/README.md` § Outputs**;
 * {@link OemDomainData} below is that table expressed as types. When the
 * extractor moves a key, the README, the `schema/` payload interface and this
 * map must change together.
 *
 * ## Caching
 *
 * One module-level promise per URL, **outside React**, shared by every
 * component instance and every hook call for the lifetime of the page. A second
 * caller arriving while a fetch is in flight joins the same promise and no
 * second request is issued. Resolved payloads are therefore also
 * **referentially stable**, which is what makes `useMemo(() => unpackTable(d.wos), [d])`
 * correct in consumers.
 *
 * Rejections are **evicted** (see {@link OEM_FETCH_CACHE}) so a failed domain
 * can be retried rather than being poisoned for the session.
 *
 * ## Rules this file keeps
 *
 * - No `Date.now()`, no `Math.random()`. Time comes from `oem-clock.ts`.
 * - Paths are **absolute from the site root** (`/data/oem/…`) — the files are
 *   Cloudflare Workers Static Assets and the app is served from nested routes.
 * - React is allowed here (this is the port's one data-fetching hook) but the
 *   cache itself is plain module state, so it also works from a loader, a
 *   transform or a test with no renderer.
 *
 * @see src/versions/v1/schema/ — every shape this file hands back
 * @see scripts/oem/README.md — the authoritative file/key table
 * @see src/versions/v1/data/core.ts — the eager half
 * @see src/versions/v1/shared/columnar.ts — rehydrating the columnar payloads
 * @see docs/foundations/4-layer-architecture.md
 */

import { useEffect, useRef, useState } from 'react';

import type { CriticalEvent, TrendSeriesRegistry } from '../schema/alert.ts';
import type { OilSample, TyreTable } from '../schema/component.ts';
import type { CostsPayload } from '../schema/cost.ts';
import type {
  CommsPayload,
  DocIndexPayload,
  LakeDocument,
  LakeIndex,
} from '../schema/document.ts';
import type { SitesPayload } from '../schema/mine.ts';
import type { PartsPayload } from '../schema/part.ts';
import type { PeoplePayload } from '../schema/person.ts';
import type { MaximoRecord, ServicePayload } from '../schema/service.ts';
import type {
  DailyMetricTable,
  EventsDailyTable,
  MachineEnergy,
  RmsPayload,
  ShiftDailyTable,
  TelemetryLivePayload,
} from '../schema/telemetry.ts';
import type { WorkOrdersPayload } from '../schema/work-order.ts';

// ===========================================================================
// The domain map
// ===========================================================================

/**
 * The twelve lazily fetched OEM+ data domains. Each names exactly one file at
 * `/data/oem/<domain>.json`.
 *
 * This union **is** the URL: {@link domainUrl} builds the path from the member,
 * so the names must stay identical to the file names the extractor writes
 * (`scripts/oem/README.md` § Outputs → Lazy).
 *
 * The two per-document families — `maximo/<id>.json` and `lake/<slug>.json` —
 * are deliberately **not** domains: they are fetched one document at a time
 * through {@link loadMachineRecord} and {@link loadLakeDoc}.
 */
export type OemDomain =
  | 'work-orders'
  | 'telemetry'
  | 'telemetry-live'
  | 'costs'
  | 'parts'
  | 'people'
  | 'service'
  | 'rms'
  | 'sites'
  | 'comms'
  | 'doc-index'
  | 'reports';

/**
 * Domain → payload shape. The typed expression of `scripts/oem/README.md`'s
 * "Lazy — `public/data/oem/`" table.
 *
 * **Ten of the twelve domains are a single schema `*Payload` interface**, which
 * is where the per-key documentation and the closed string vocabularies live.
 * Follow the link to read a domain's contents; nothing is restated here.
 *
 * The two exceptions are composed inline from schema types, and are marked
 * below:
 *
 * - `telemetry` — nine keys spread across `alert.ts`, `component.ts` and
 *   `telemetry.ts`, with no single owning module and so no payload interface.
 * - `reports` — seven heterogeneous report documents with no schema module at
 *   all yet.
 *
 * Two conventions hold throughout, both enforced by the schema types:
 *
 * - **Columnar keys are tables, not arrays.** They arrive in the demo's
 *   `{cols, rows}` wire form and the consumer calls `unpackTable()`. The
 *   schema names the hydrated row beside the table — `WorkOrderTable` beside
 *   `WorkOrder` — so the call reads `unpackTable<WorkOrder>(data.wos)`. See
 *   `columnar.ts` for which eight tables and why.
 * - **Keyed collections stay objects.** `docidx`, `rms_health`, `siteinfo`,
 *   `live`, `reports`, `email_bodies`, `transcripts` and `vtts` are maps in the
 *   source and are not flattened into arrays here.
 *
 * Indexed by {@link useOemData}'s type parameter, so `useOemData('parts').data`
 * is `PartsPayload | null` with no annotation at the call site.
 */
export interface OemDomainData {
  /** 811 KB, key `wos`. The port's largest single fetch. */
  'work-orders': WorkOrdersPayload;
  /**
   * 461 KB. **Composed inline** — the nine keys are owned by three schema
   * modules, so there is no single `TelemetryPayload` to point at.
   */
  telemetry: {
    /** Keyed by machine id — one open alert's series. */
    alert_series: TrendSeriesRegistry;
    /** 1,100 × 5, columnar. Unpacks to `DailyMetricRow`. */
    daily_metric: DailyMetricTable;
    /** 100 rows, one per machine. */
    energy: readonly MachineEnergy[];
    /** 41 rows — the un-aggregated log behind `events_daily`. */
    events_critical: readonly CriticalEvent[];
    /** 2,364 × 5, columnar. Unpacks to `EventDailyRow`. */
    events_daily: EventsDailyTable;
    /** Keyed by machine id — two closed incidents' series. */
    incident_series: TrendSeriesRegistry;
    /** 513 rows. */
    oil: readonly OilSample[];
    /** 1,100 × 9, columnar. Unpacks to `ShiftDailyRow`. */
    shift_daily: ShiftDailyTable;
    /** 282 × 12, columnar. Unpacks to `TyreInspection`. */
    tyres: TyreTable;
  };
  /**
   * 70 KB. The "where does the data come from" domain: two machines' live
   * sample streams plus one raw payload of each integration format, shown
   * verbatim on the integrations surfaces.
   */
  'telemetry-live': TelemetryLivePayload;
  /** 88 KB. */
  costs: CostsPayload;
  /** 97 KB. */
  parts: PartsPayload;
  /**
   * 16 KB. `css` and `roster` live here rather than in a survey file of their
   * own — both are people-shaped and tiny (`scripts/oem/README.md` §
   * Placements worth knowing).
   */
  people: PeoplePayload;
  /**
   * 317 KB. `mx_pm`, `maximo_assets` and `mx_contracts` appear here **whole**
   * for fleet-wide views, and again per machine inside {@link MaximoRecord}.
   */
  service: ServicePayload;
  /** 239 KB. */
  rms: RmsPayload;
  /** 30 KB, key `siteinfo`, keyed by `MineCode`. */
  sites: SitesPayload;
  /** 58 KB. */
  comms: CommsPayload;
  /** 327 KB. */
  'doc-index': DocIndexPayload;
  /**
   * 92 KB. The report *content* only; the rendered DOCX/PPTX blobs were
   * dropped at extract time (`scripts/oem/README.md` § Outputs).
   *
   * **Composed inline, and the one domain with no schema module.** The seven
   * reports (`deck_br`, `drills`, `email_mi`, `incidents`, `loadhaul`,
   * `surface`, `week`) each have their **own** shape — a weekly rollup, a
   * division summary and a drafted email have nothing structural in common —
   * so a report is left as an open record and narrowed at the point of use.
   *
   * This belongs in a `schema/report.ts` as a discriminated union keyed by
   * report id; until that exists there is nothing in layer 1 to point at, and
   * inventing the union here would be exactly the duplication this file no
   * longer does.
   */
  reports: {
    /** Report id → that report's content. Seven reports. */
    reports: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
  };
}

// ===========================================================================
// Fetch cache
// ===========================================================================

/**
 * Root of the extracted OEM+ data, **absolute from the site root**.
 *
 * Absolute because the app is served from nested routes (`/v1/fleet/L-02`) and
 * the files are Cloudflare Workers Static Assets served from the deploy root —
 * a relative path would resolve against whatever route the user happened to be
 * on.
 */
const OEM_DATA_ROOT = '/data/oem';

/** URL of a domain file. */
const domainUrl = (domain: OemDomain): string => `${OEM_DATA_ROOT}/${domain}.json`;

/**
 * URL → in-flight or settled fetch promise. One entry per URL, for the page's
 * lifetime.
 *
 * Deliberately module state, not React state: the hook must be able to hand a
 * second component the *same* promise even when the first component that asked
 * has already unmounted, and the loaders below must work with no renderer at
 * all.
 *
 * Values are `Promise<unknown>` rather than a per-URL type — the map cannot be
 * heterogeneously typed — and {@link fetchJson} restores the type at the one
 * boundary where the URL and the expected shape are both known.
 */
const OEM_FETCH_CACHE = new Map<string, Promise<unknown>>();

/**
 * Fetch and parse one JSON file, sharing the request with any caller already
 * waiting on the same URL.
 *
 * A **rejected** promise is evicted from the cache as it settles, so a failed
 * URL can be retried by simply calling again. (Evicting inside the `catch`
 * rather than refusing to cache rejections at all is what keeps concurrent
 * callers on one request: they all join the same in-flight promise and all see
 * the same failure, and only the *next* caller starts a new attempt.)
 *
 * @typeParam T - the expected payload shape.
 * @param url - absolute, site-root-relative path.
 * @returns the parsed payload.
 * @throws `Error` naming the status and the URL when the response is not `ok`.
 */
function fetchJson<T>(url: string): Promise<T> {
  const cached = OEM_FETCH_CACHE.get(url);
  if (cached) return cached as Promise<T>;

  const pending = fetch(url)
    .then((res) => {
      if (!res.ok) {
        throw new Error(`OEM data fetch failed: ${res.status} ${res.statusText} — ${url}`);
      }
      return res.json() as Promise<T>;
    })
    .catch((cause: unknown) => {
      // Evict so the next caller retries rather than re-reading the failure.
      OEM_FETCH_CACHE.delete(url);
      throw cause instanceof Error
        ? cause
        : new Error(`OEM data fetch failed: ${String(cause)} — ${url}`);
    });

  OEM_FETCH_CACHE.set(url, pending);
  return pending;
}

/**
 * Drop cached entries so the next request refetches.
 *
 * Failed URLs evict themselves, so this is only for the rare caller that wants
 * to discard a *successful* payload — a dev-tools "reload data" affordance, or
 * a test isolating fetch counts. Pass no argument to clear everything.
 *
 * @param url - the exact URL to evict, or omit to clear the whole cache.
 */
export function clearOemDataCache(url?: string): void {
  if (url === undefined) OEM_FETCH_CACHE.clear();
  else OEM_FETCH_CACHE.delete(url);
}

// ===========================================================================
// Single-document loaders
// ===========================================================================

/**
 * Fetch one machine's Maximo bundle — `/data/oem/maximo/<machineId>.json`.
 *
 * One of 100 files, ~15 KB each. Cached and de-duplicated like every other URL,
 * so a machine page and a drawer opened on the same machine share one request.
 *
 * `mx_pm`, `maximo_asset` and `mx_contract` on the returned
 * {@link MaximoRecord} are **duplicated** from `service.json`: whole there for
 * fleet-wide views, per-machine here to save 100 round trips on the machine
 * pages (`scripts/oem/README.md` § Placements worth knowing).
 *
 * @param machineId - fleet machine id, e.g. `'L-02'`. Must be a known id;
 *   an unknown one produces a 404 and a rejected promise.
 */
export function loadMachineRecord(machineId: string): Promise<MaximoRecord> {
  return fetchJson<MaximoRecord>(`${OEM_DATA_ROOT}/maximo/${machineId}.json`);
}

/**
 * Fetch one raw source document from the data lake —
 * `/data/oem/lake/<slug>.json`.
 *
 * @param slug - the document's slug, as `LakeEntry.slug` gives it. Slugs are
 *   the kebab-cased `path`, because file names are not unique; get one from
 *   {@link loadLakeIndex} rather than constructing it.
 */
export function loadLakeDoc(slug: string): Promise<LakeDocument> {
  return fetchJson<LakeDocument>(`${OEM_DATA_ROOT}/lake/${slug}.json`);
}

/**
 * Fetch the data lake's payload-free catalogue —
 * `/data/oem/lake/index.json`.
 *
 * The data browser lists all 271 documents from this one fetch and calls
 * {@link loadLakeDoc} only when a document is opened; downloading the lake
 * itself would be 2.0 MB.
 */
export function loadLakeIndex(): Promise<LakeIndex> {
  return fetchJson<LakeIndex>(`${OEM_DATA_ROOT}/lake/index.json`);
}

/**
 * Fetch one domain file directly, outside React — the imperative half of
 * {@link useOemData}, for transforms, route loaders and tests.
 *
 * @typeParam K - the domain, which fixes the payload type.
 */
export function loadOemDomain<K extends OemDomain>(domain: K): Promise<OemDomainData[K]> {
  return fetchJson<OemDomainData[K]>(domainUrl(domain));
}

// ===========================================================================
// The hook
// ===========================================================================

/** What {@link useOemData} returns. Exactly one of the three is meaningful. */
export interface OemDataState<K extends OemDomain> {
  /** The payload, or `null` until it has loaded (or if it failed). */
  data: OemDomainData[K] | null;
  /** `true` while a fetch is outstanding. */
  loading: boolean;
  /** The failure, or `null`. Set once `loading` goes false without `data`. */
  error: Error | null;
}

/**
 * Load one lazy OEM+ domain, keyed by name.
 *
 * ```tsx
 * const { data, loading, error } = useOemData('parts');
 * if (loading) return <Spinner />;
 * if (error) return <DataError error={error} />;
 * if (!data) return null;
 * data.inventory.filter((r) => r.qty_on_hand < r.min_level);  // typed
 * ```
 *
 * ### Behaviour
 *
 * - **One request per domain per page.** Ten components asking for
 *   `'telemetry'` produce one fetch; the tenth to mount gets the already
 *   resolved payload.
 * - **Referentially stable `data`.** Re-renders and remounts hand back the same
 *   object, so `useMemo(() => unpackTable<WorkOrder>(data.wos), [data])` is
 *   correct.
 * - **Synchronous on a cache hit.** A domain already resolved earlier in the
 *   session still settles a microtask later (promises always do), so the first
 *   render reports `loading: true`. Render a skeleton, not a layout jump.
 * - **No state after unmount**, and no state from a stale domain: the effect
 *   guards on both a liveness ref and the domain it was started for, so
 *   switching domains mid-flight cannot let the first response overwrite the
 *   second.
 * - **Retryable failures.** A failed URL is evicted from the cache, so a
 *   remount (or any later caller) starts a fresh request.
 *
 * @typeParam K - the domain name; fixes `data` to that domain's payload type.
 * @param domain - which domain to load.
 */
export function useOemData<K extends OemDomain>(domain: K): OemDataState<K> {
  const [state, setState] = useState<OemDataState<K>>({
    data: null,
    loading: true,
    error: null,
  });

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    setState({ data: null, loading: true, error: null });

    loadOemDomain(domain).then(
      (payload) => {
        if (!current || !mounted.current) return;
        setState({ data: payload, loading: false, error: null });
      },
      (cause: unknown) => {
        if (!current || !mounted.current) return;
        setState({
          data: null,
          loading: false,
          error: cause instanceof Error ? cause : new Error(String(cause)),
        });
      },
    );

    return () => {
      current = false;
    };
  }, [domain]);

  return state;
}

// ===========================================================================
// Schema re-exports
// ===========================================================================
//
// The shapes a scene author needs alongside the hook, so one import line does:
//
//     import { useOemData, type WorkOrder } from './useOemData';
//
// Types only, no runtime cost. `schema/` remains the single source of truth —
// these are pass-throughs, not declarations. The list is explicit rather than
// `export *` so that what travels with the hook is visible here; reach past it
// into `../schema/<entity>` for anything it does not carry.

/** Machine identity vocabularies — the keys almost every payload joins on. */
export type { MachineClass, MineCode, ModelCode } from '../schema/machine.ts';

/** `work-orders.json`. */
export type {
  AwaitingPartsStatus,
  ClosedWorkOrderStatus,
  FailureCause,
  PmCode,
  WorkOrder,
  WorkOrderCells,
  WorkOrderPriority,
  WorkOrdersPayload,
  WorkOrderStatus,
  WorkOrderTable,
  WorkOrderType,
} from '../schema/work-order.ts';

/** `telemetry.json` — the columnar tables, their rows, and the energy rollup. */
export type {
  DailyMetricName,
  DailyMetricRow,
  DailyMetricTable,
  EventDailyRow,
  EventsDailyTable,
  MachineEnergy,
  ShiftDailyRow,
  ShiftDailyTable,
} from '../schema/telemetry.ts';

/** `telemetry.json` — the component-condition family. */
export type {
  ComponentHealth,
  ComponentName,
  OilCompartment,
  OilSample,
  OilVerdict,
  TyreInspection,
  TyrePosition,
  TyreSize,
  TyreTable,
  WorkOrderComponentName,
} from '../schema/component.ts';

/** `telemetry.json` — critical events and the trend series. */
export type {
  CriticalEvent,
  CriticalEventSeverity,
  CriticalEventSource,
  EventSeverity,
  TrendPoint,
  TrendSeries,
  TrendSeriesRegistry,
} from '../schema/alert.ts';

/** `telemetry-live.json`. */
export type {
  LiveAlarmCode,
  LiveLoaderSample,
  LiveReplayRegistry,
  LiveSample,
  LiveSampleBase,
  LiveTruckSample,
  MqttEnvelope,
  MqttTelemetryPayload,
  TelemetryLivePayload,
} from '../schema/telemetry.ts';

/** `costs.json`. */
export type {
  CostByCrewCategory,
  CostByMachineCategory,
  CostByMonthMine,
  CostCategory,
  CostsPayload,
  Timesheet,
} from '../schema/cost.ts';

/** `parts.json`. */
export type {
  Bulletin,
  BulletinId,
  CataloguePart,
  CatbaseBomLine,
  CatbaseSection,
  InventoryLine,
  ModelFamily,
  PartCriticality,
  PartOrder,
  PartsPayload,
  StoreLocation,
} from '../schema/part.ts';

/** `people.json`. */
export type {
  CssCells,
  CssTable,
  CustomerSatisfactionResponse,
  HomeSite,
  PeoplePayload,
  PersonOrg,
  PersonRecord,
  RosterCells,
  RosterEntry,
  RosterTable,
  TechnicianPresence,
  TechnicianStatus,
} from '../schema/person.ts';

/** `service.json` and `maximo/<MACHINE_ID>.json`. */
export type {
  CaseType,
  KnowledgeArticle,
  MaximoAsset,
  MaximoContract,
  MaximoCustomerAsset,
  MaximoFailureReport,
  MaximoPm,
  MaximoPmRow,
  MaximoRecord,
  MaximoSiteId,
  RemoteSession,
  RemoteSessionOutcome,
  ServiceCase,
  ServicePayload,
  TechnicianJob,
  TechnicianJobStatus,
  TechnicianJobType,
  TrainingRecord,
  TrainingStatus,
} from '../schema/service.ts';

/** `rms.json`. */
export type {
  RmsBehaviourRow,
  RmsDailyDrillRow,
  RmsHealthRegistry,
  RmsLiveDrillSample,
  RmsMachineHealth,
  RmsPayload,
  RmsStatusRow,
  RmsTorqueRow,
} from '../schema/telemetry.ts';

/** `rms.json` — the analyst alerts, which belong with the alert family. */
export type { RmsAlert, RmsAlertSeverity, RmsOpenAlert } from '../schema/alert.ts';

/** `sites.json`. */
export type { SiteInfo, SiteInfoRegistry, SitesPayload } from '../schema/mine.ts';

/** `comms.json`, `doc-index.json`, and the `lake/` family. */
export type {
  CommsPayload,
  ConnectorLabel,
  DocIndexEntry,
  DocIndexPayload,
  DocIndexRef,
  DocIndexRegistry,
  DocumentKind,
  EmailHeader,
  LakeCell,
  LakeDocument,
  LakeEntry,
  LakeFileCollision,
  LakeIndex,
  LakeSheet,
  LibraryDocument,
  LibraryFolder,
  MeetingRecord,
  VideoKind,
  VideoRecord,
} from '../schema/document.ts';
